import {
  FetchReviewsParams,
  ProductReviewsResponse,
  ProductReviewSummaryResponse,
  AppliedReviewFilter,
  ReviewFilter,
  ProductReviewFeedbacks,
  ReviewSort,
  ReviewSortOption,
  SecondaryInfo,
  ProductReviewVote,
  ProductFeedbackType,
  ProductReviewVoteData,
} from '../templates/product/types/reviews';
import { aloApiFetch } from 'src/scripts/utils/alo-api-fetch';
import { getCookie, setCookie } from './cookies';

// Sort options mapping
export const REVIEW_SORT_OPTIONS: Record<ReviewSort, string> = {
  [ReviewSort.RatingDesc]: 'Highest to Lowest Rating',
  [ReviewSort.RatingAsc]: 'Lowest to Highest Rating',
  [ReviewSort.Relevancy]: 'Most Relevant',
  [ReviewSort.HelpfulnessDesc]: 'Most Helpful',
  [ReviewSort.SubmissionTimeDesc]: 'Most Recent',
};

export const REVIEW_SORT_OPTIONS_ARRAY: ReviewSortOption[] = Object.entries(REVIEW_SORT_OPTIONS).map(
  ([value, label]) => ({
    label,
    // know that value is a key of ReviewSort, so we can safely cast it
    value: value as ReviewSort,
  })
);

const reviewEndpointPrefix = '/v1/review';
const reviewFeedbackCookieName = 'product-review-feedback';

export const fetchProductReviews = async ({
  productHandle = '',
  offset = 0,
  limit = 10,
  sort = ReviewSort.Relevancy,
  feature = '',
  q = '',
  filters,
  signal,
}: FetchReviewsParams & { signal?: AbortSignal }): Promise<ProductReviewsResponse> => {
  const params: Record<string, string> = {
    productHandle,
    offset: String(offset),
    limit: String(limit),
    sort: String(sort),
  };

  if (filters.length > 0) {
    filters.forEach((filter) => {
      params[filter.key] = filter.values.join(',');
    });
  }

  if (feature) {
    params.feature = feature;
  }

  if (q) {
    params.q = q;
  }

  const queryParams = new URLSearchParams(params);

  try {
    const response = await aloApiFetch(`${reviewEndpointPrefix}/product_reviews?${queryParams.toString()}`, {
      method: 'GET',
      signal,
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data: ProductReviewsResponse = await response.json();
    return data;
  } catch (error) {
    throw error;
  }
};

export const fetchProductReviewsHeaderData = async ({
  productHandle = '',
}: {
  productHandle: string;
}): Promise<ProductReviewSummaryResponse> => {
  try {
    const response = await aloApiFetch(
      `${reviewEndpointPrefix}/product_summary?productHandle=${encodeURIComponent(productHandle)}`
    );
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result: ProductReviewSummaryResponse = await response.json();
    return result;
  } catch (error) {
    throw error;
  }
};

export const destructureSecondaryInfo = (secondary: SecondaryInfo[]) => {
  const sizeInfo: SecondaryInfo[] = [];
  const otherInfo: SecondaryInfo[] = [];

  secondary.forEach((item) => {
    if (item.label === 'Usual Size' || item.label === 'Size Ordered') {
      sizeInfo.push(item);
    } else {
      otherInfo.push(item);
    }
  });

  return { sizeInfo, otherInfo };
};

export const reviewSidebarBodySelector = '.product-review-filter-drawer-wrapper .alo-sidebar-body';
export const reviewSidebarHeaderSelector = '.product-review-filter-drawer-wrapper .alo-sidebar-header';

export const reportReview = async (contentId: number): Promise<{ success: boolean }> => {
  try {
    const response = await aloApiFetch(`${reviewEndpointPrefix}/report`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ content_id: contentId }),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return {
      success: true,
    };
  } catch (error) {
    console.error('Error recording report:', error);
    return {
      success: false,
    };
  }
};

export function updateAppliedReviewFiltersArray(
  filters: AppliedReviewFilter[],
  key: string,
  value: string,
  checkout: boolean
): AppliedReviewFilter[] {
  const index = filters.findIndex((f) => f.key === key);

  if (index === -1) {
    if (checkout) {
      return [...filters, { key, values: [value] }];
    } else {
      return filters;
    }
  }

  const filter = { ...filters[index] };
  const valuesSet = new Set(filter.values);

  if (checkout) {
    valuesSet.add(value);
  } else {
    valuesSet.delete(value);
  }

  const updatedValues = Array.from(valuesSet);

  if (updatedValues.length === 0) {
    return [...filters.slice(0, index), ...filters.slice(index + 1)];
  }
  const updatedFilter = { key, values: updatedValues };
  return [...filters.slice(0, index), updatedFilter, ...filters.slice(index + 1)];
}

export const getSelectedProductReviewFiltersCount = (filter: ReviewFilter, selectedFilters: AppliedReviewFilter[]) => {
  const selectedFilter = selectedFilters.find((selected) => filter.filtertype.label === selected.key);
  return selectedFilter ? selectedFilter.values.length : 0;
};

export const getProductReviewFiltersCount = (
  selectedFilters: AppliedReviewFilter[],
  activeKeyword: string | undefined
) => {
  const filterCount = selectedFilters.reduce((acc, f) => acc + f.values.length, 0);
  return activeKeyword ? filterCount + 1 : filterCount;
};
export const postReviewVoting = async (content_id: number, vote: ProductReviewVote) => {
  try {
    const response = await aloApiFetch(`${reviewEndpointPrefix}/vote_helpful`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        content_id,
        vote,
      }),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return {
      success: true,
    };
  } catch (error) {
    console.error('Error recording vote:', error);
    return {
      success: false,
    };
  }
};

export const getProductReviewFeedbackKeyName = (content_id: number, isReport?: boolean) =>
  isReport ? `review-i-${content_id}` : `review-h-${content_id}`;

export const updateReviewFeedback = (input: {
  content_id: number;
  feedbackType: ProductFeedbackType;
  count?: number;
}) => {
  const { content_id, feedbackType, count = 0 } = input;
  const feedbacks: ProductReviewFeedbacks = getProductReviewFeedbackCookieValue();
  const keyName = getProductReviewFeedbackKeyName(content_id, feedbackType === 'report');
  switch (feedbackType) {
    case 'positive':
      feedbacks[keyName] = generateVotingFeedbackCookieValue(feedbackType, count);
      break;
    case 'negative':
      feedbacks[keyName] = generateVotingFeedbackCookieValue(feedbackType, count);
      break;
    case 'report':
      feedbacks[keyName] = true;
      break;
    default:
      break;
  }
  setProductReviewFeedbackCookieValue(feedbacks);
  return feedbacks[keyName];
};

export const generateVotingFeedbackCookieValue = (vote: ProductReviewVote, count: number): ProductReviewVoteData => ({
  c: count + 1,
  v: vote === ProductReviewVote.Positive ? 'p' : 'n',
});

export function setProductReviewFeedbackCookieValue(feedback: ProductReviewFeedbacks) {
  // Set the cookie value for review feedback
  try {
    setCookie(reviewFeedbackCookieName, JSON.stringify(feedback));
  } catch (error) {
    console.error('Error setting review feedback cookie:', error);
  }
}

export function getProductReviewFeedbackCookieValue(): ProductReviewFeedbacks {
  // Retrieve the cookie value for review feedback
  const cookieValue = getCookie(reviewFeedbackCookieName);
  if (cookieValue) {
    try {
      return JSON.parse(cookieValue);
    } catch (error) {
      console.error('Error parsing review feedback cookie:', error);
      return {};
    }
  }
  return {};
}
